// The sandbox in the console (D64, D105): example data a signed-in person opens from inside the
// console (first run, the scope menu). The console asks the API for a read-only token for the
// sandbox's one project (POST /v1/sandbox/token, which answers a console session only) and
// sends it only on that project's read requests, so it cannot reach anything else, and the API
// refuses it on every write route. The token and the project's names live in sessionStorage
// for this tab: a per-tab convenience that the page re-creates when it is missing or about to
// expire.
import { API_BASE, ApiError } from './api/client'
import type { Platform } from './api/types'

export interface SandboxState {
  token: string
  /** Epoch ms. */
  expiresAt: number
  orgId: string
  orgName: string
  projectId: string
  projectName: string
  apps: { id: string; name: string; platform: Platform; bundle_id: string }[]
}

const KEY = 'apsio.sandbox'
/** A token this close to its end is replaced before use. */
const REFRESH_MS = 10 * 60_000
let memory: SandboxState | undefined

export function readSandbox(): SandboxState | undefined {
  if (memory) return memory
  try {
    const text = sessionStorage.getItem(KEY)
    memory = text ? (JSON.parse(text) as SandboxState) : undefined
  } catch {
    memory = undefined
  }
  return memory
}

function save(state: SandboxState | undefined) {
  memory = state
  try {
    if (state) sessionStorage.setItem(KEY, JSON.stringify(state))
    else sessionStorage.removeItem(KEY)
  } catch {
    // Storage blocked: the sandbox lasts for this page only.
  }
}

export function leaveSandbox() {
  save(undefined)
}

/** The sandbox, when this project is the sandbox's (and its token is still valid). */
export function sandboxFor(
  projectId: string | undefined,
  now = Date.now(),
): SandboxState | undefined {
  const s = readSandbox()
  return s && projectId && s.projectId === projectId && s.expiresAt > now ? s : undefined
}

/** The sandbox an org/project path belongs to, from the console's own route shape. */
export function sandboxForPath(pathname: string): SandboxState | undefined {
  const m = /^\/o\/([^/]+)\/(?:p\/([^/]+))?/.exec(pathname)
  const s = readSandbox()
  if (!m || !s || m[1] !== s.orgId) return undefined
  return !m[2] || m[2] === s.projectId ? s : undefined
}

async function json<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string }
  }
  if (!res.ok)
    throw new ApiError(
      res.status,
      body.error?.code ?? 'unknown',
      body.error?.message ?? res.statusText,
    )
  return body as T
}

export interface SandboxToken {
  token: string
  expires_at: string
  project_id: string
}

/**
 * A new sandbox token, minted for this console session (D105): the one the sandbox's pages use,
 * or one the Agents page gives for the CLI or MCP.
 */
export async function mintSandboxToken(): Promise<SandboxToken> {
  return json<SandboxToken>(
    // The session cookie says who asks; the header makes the mint a preflighted request.
    await fetch(`${API_BASE}/sandbox/token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Apsio-Sandbox': '1' },
    }),
  )
}

/**
 * A fresh sandbox token and its project, saved for this tab. Kept when the current one has
 * more than ten minutes left.
 */
export async function startSandbox(now = Date.now()): Promise<SandboxState> {
  const current = readSandbox()
  if (current && current.expiresAt - now > REFRESH_MS) return current
  const minted = await mintSandboxToken()
  const { projects } = await json<{
    projects: {
      id: string
      name: string
      org_id: string
      org_name: string
      apps: SandboxState['apps']
    }[]
  }>(
    await fetch(`${API_BASE}/projects`, {
      headers: { authorization: `Bearer ${minted.token}` },
      credentials: 'omit',
    }),
  )
  const project = projects.find((p) => p.id === minted.project_id)
  if (!project) throw new ApiError(503, 'unavailable', 'The sandbox has no project.')
  const state: SandboxState = {
    token: minted.token,
    expiresAt: Date.parse(minted.expires_at),
    orgId: project.org_id,
    orgName: project.org_name,
    projectId: project.id,
    projectName: project.name,
    apps: project.apps,
  }
  save(state)
  return state
}

/**
 * The Authorization header for a read (GET or HEAD) request, when it is for the sandbox's project. Every
 * other request goes with the session cookie as usual, so a signed-in person browsing the
 * sandbox never sends the sandbox token anywhere else.
 */
export function sandboxHeader(url: string, method = 'GET'): string | undefined {
  // Reads only: the token is read-only anyway, and a write never carries it.
  if (method !== 'GET' && method !== 'HEAD') return undefined
  const s = readSandbox()
  if (!s || s.expiresAt <= Date.now()) return undefined
  const path = new URL(url, 'http://x').pathname
  return path.startsWith(`/v1/projects/${s.projectId}/`) ? `Bearer ${s.token}` : undefined
}
